// Audit · License Events — fleet-wide lifecycle history (issued/renewed/revoked/
// suspended…). Read-only. Data: /analytics/license-events.
import React, { useEffect, useState } from 'react';
import { PageHeader, Card, Table, Td, Row, Mono, Btn } from '@/components/shared/primitives';
import { Pill } from '@/components/shared/badges';
import { EmptyState } from '@/components/shared/EmptyState';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { CompanyChip } from '@/components/shared/CompanyChip';
import { MaskedKey } from '@/components/shared/MaskedKey';
import { getLicenseEvents, type LicenseEventRow } from '@/lib/api/audit';

const LC: Record<string, { color: string; bg: string }> = {
  issued: { color: 'var(--ps-pill-green-tx)', bg: 'var(--ps-pill-green-bg)' }, activated: { color: 'var(--ps-pill-green-tx)', bg: 'var(--ps-pill-green-bg)' },
  renewed: { color: 'var(--ps-pill-blue-tx)', bg: 'var(--ps-pill-blue-bg)' }, reinstated: { color: 'var(--ps-pill-blue-tx)', bg: 'var(--ps-pill-blue-bg)' },
  escalated: { color: 'var(--ps-pill-blue-tx)', bg: 'var(--ps-pill-blue-bg)' }, revoked: { color: 'var(--ps-pill-red-tx)', bg: 'var(--ps-pill-red-bg)' },
  suspended: { color: 'var(--ps-pill-amber-tx)', bg: 'var(--ps-pill-amber-bg)' }, expired: { color: 'var(--ps-pill-amber-tx)', bg: 'var(--ps-pill-amber-bg)' },
  validated: { color: 'var(--ps-pill-gray-tx)', bg: 'var(--ps-pill-gray-bg)' },
};

export function AuditLifecycle() {
  const [rows, setRows] = useState<LicenseEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getLicenseEvents(200).then(setRows).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load events')).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <PageHeader title="License Events" subtitle="Full lifecycle history — issued, renewed, suspended, revoked" actions={<Btn icon="download" disabled title="Lifecycle export is not yet supported.">Export</Btn>} />
      {error && <div style={{ marginBottom: 16 }}><AlertBanner kind="error">{error}</AlertBanner></div>}
      <Card style={{ overflow: 'hidden' }}>
        {loading ? <div style={pad}>Loading…</div> : rows.length === 0 ? (
          <EmptyState icon="history" title="No license events" body="License lifecycle events (issued, renewed, suspended, revoked) will appear here as they happen." />
        ) : (
          <Table cols={[{ label: 'Event' }, { label: 'License' }, { label: 'Account' }, { label: 'Reason' }, { label: 'When' }]}>
            {rows.map((l) => {
              const c = LC[l.event_type?.toLowerCase()] || { color: 'var(--text-muted)', bg: 'var(--bg-muted)' };
              return (
                <Row key={l.id}>
                  <Td><Pill bg={c.bg} fg={c.color}>{l.event_type}</Pill></Td>
                  <Td><MaskedKey value={l.license?.license_id} size={12} /></Td>
                  <Td><CompanyChip id={l.license?.organization?.id} name={l.license?.organization?.name} origin={{ type: 'license-events' }} icon={false} /></Td>
                  <Td style={{ color: 'var(--text-secondary)', fontSize: 12.5 }}>{(l.event_data?.reason as string) || (l.event_data?.note as string) || '—'}</Td>
                  <Td><Mono style={{ fontSize: 12, color: 'var(--text-muted)' }}>{new Date(l.created_at).toLocaleString()}</Mono></Td>
                </Row>
              );
            })}
          </Table>
        )}
      </Card>
    </div>
  );
}
const pad: React.CSSProperties = { padding: 40, textAlign: 'center', color: 'var(--text-muted)' };
export default AuditLifecycle;
